<template>
  <q-page padding>
    <div class="row items-center justify-between q-mb-md">
      <div>
        <div class="text-h5">{{ $t('composites.PartnerDedup.title') }}</div>
        <div class="text-body2 text-grey-7">{{ $t('composites.PartnerDedup.subtitle') }}</div>
      </div>
      <div class="row q-gutter-sm items-center">
        <q-btn
          v-if="obviousCount > 0"
          unelevated
          color="positive"
          :icon="symRoundedAutoAwesomeMotion"
          :label="$t('composites.PartnerDedup.auto_merge', { count: obviousCount })"
          :loading="autoMerging"
          @click="onAutoMerge"
        />
        <q-btn
          unelevated
          color="primary"
          :icon="symRoundedManageSearch"
          :label="scanning ? $t('composites.PartnerDedup.scanning') : $t('composites.PartnerDedup.scan_now')"
          :loading="scanning"
          @click="onScan"
        />
        <q-btn
          flat
          :icon="symRoundedRefresh"
          :label="$t('composites.PartnerDedup.refresh')"
          :loading="loading"
          @click="reload"
        />
      </div>
    </div>

    <q-input
      v-model="searchQuery"
      :placeholder="$t('composites.PartnerDedup.search_placeholder')"
      dense
      outlined
      clearable
      class="q-mb-md"
      @update:model-value="onSearchInput"
    >
      <template #prepend>
        <q-icon :name="symRoundedSearch" />
      </template>
    </q-input>

    <q-banner v-if="error" class="bg-red-2 text-red-10 q-mb-md" rounded>
      {{ error }}
    </q-banner>

    <q-banner v-if="!clusters.length && !loading" class="bg-grey-2 text-grey-9" rounded>
      {{ $t('composites.PartnerDedup.no_candidates') }}
    </q-banner>

    <DedupClusterCard
      v-for="cluster in clusters"
      :key="cluster.keeper.id"
      :cluster="cluster"
      @merge="onClusterMergeWithWinners"
      @preview-merge="onPreviewMerge"
    />

    <MergeConfirmDialog
      v-model="confirmOpen"
      :keeper="pendingMerge?.keeper ?? null"
      :loser="pendingMerge?.loser ?? null"
      :load-preview="previewMerge"
      @confirm="onPreviewConfirm"
    />
  </q-page>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useQuasar } from 'quasar'
import { useI18n } from 'vue-i18n'
import {
  symRoundedAutoAwesomeMotion,
  symRoundedManageSearch,
  symRoundedRefresh,
  symRoundedSearch,
} from '@quasar/extras/material-symbols-rounded'
import DedupClusterCard from './DedupClusterCard.vue'
import MergeConfirmDialog from './MergeConfirmDialog.vue'
import { usePartnerDedup, type PartnerSummary } from './usePartnerDedup'

const {
  clusters,
  loading,
  scanning,
  autoMerging,
  obviousCount,
  error,
  load,
  scan,
  autoMerge,
  previewMerge,
  mergeOne,
  mergeCluster,
} = usePartnerDedup()
const $q = useQuasar()
const { t } = useI18n()

const searchQuery = ref('')
let searchDebounce: ReturnType<typeof setTimeout> | null = null

const confirmOpen = ref(false)
const pendingMerge = ref<{ keeper: PartnerSummary; loser: PartnerSummary } | null>(null)

function onPreviewMerge(keeperId: string, loserId: string) {
  for (const cluster of clusters.value) {
    if (cluster.keeper.id !== keeperId) continue
    const loser = cluster.losers.find((l) => l.id === loserId)
    if (!loser) return
    pendingMerge.value = { keeper: cluster.keeper, loser }
    confirmOpen.value = true
    return
  }
}

async function onPreviewConfirm(resolutions: Record<string, 'keeper' | 'loser'>) {
  if (!pendingMerge.value) return
  const { keeper, loser } = pendingMerge.value
  try {
    await mergeOne(keeper.id, loser.id, resolutions)
    confirmOpen.value = false
    pendingMerge.value = null
    await load({ search: searchQuery.value || undefined })
    $q.notify({ type: 'positive', message: t('composites.PartnerDedup.cluster.merge_done', { merged: 1, failed: 0 }) })
  } catch (e) {
    $q.notify({
      type: 'negative',
      message: t('composites.PartnerDedup.errors.merge_failed', { message: (e as Error).message }),
    })
  }
}

onMounted(() => {
  void load()
})

function reload() {
  void load({ search: searchQuery.value || undefined })
}

function onSearchInput(value: string | number | null) {
  searchQuery.value = (value ?? '').toString()
  if (searchDebounce) clearTimeout(searchDebounce)
  searchDebounce = setTimeout(() => {
    void load({ search: searchQuery.value || undefined })
  }, 300)
}

async function onScan() {
  await scan()
  $q.notify({ type: 'info', message: t('composites.PartnerDedup.scan_started') })
}

async function onClusterMergeWithWinners(
  keeperId: string,
  loserIds: string[],
  fieldWinners: Record<string, string>,
  convertToSupplier: boolean,
) {
  const overrideCount = Object.keys(fieldWinners).filter((k) => fieldWinners[k] && fieldWinners[k] !== keeperId).length
  $q.dialog({
    title: t('composites.PartnerDedup.confirm.title'),
    message: t('composites.PartnerDedup.cluster.confirm_body_with_winners', {
      count: loserIds.length,
      overrides: overrideCount,
    }),
    cancel: true,
    persistent: true,
    ok: { color: 'negative', label: t('composites.PartnerDedup.confirm.confirm') },
  }).onOk(async () => {
    try {
      const result = await mergeCluster(keeperId, loserIds, {}, fieldWinners, convertToSupplier)
      if (result) {
        $q.notify({
          type: 'positive',
          message: t('composites.PartnerDedup.cluster.merge_done', {
            merged: result.merged,
            failed: result.failed.length,
          }),
        })
        if (result.type_changed) {
          $q.notify({
            type: 'info',
            message: t('composites.PartnerDedup.cluster.type_changed_to_supplier'),
          })
        }
        await load({ search: searchQuery.value || undefined })
      }
    } catch (e) {
      $q.notify({
        type: 'negative',
        message: t('composites.PartnerDedup.errors.merge_failed', { message: (e as Error).message }),
      })
    }
  })
}

async function onAutoMerge() {
  $q.dialog({
    title: t('composites.PartnerDedup.auto_merge_confirm.title'),
    message: t('composites.PartnerDedup.auto_merge_confirm.body', { count: obviousCount.value }),
    cancel: true,
    persistent: true,
    ok: { color: 'positive', label: t('composites.PartnerDedup.auto_merge_confirm.confirm') },
  }).onOk(async () => {
    const result = await autoMerge()
    if (result) {
      $q.notify({
        type: 'positive',
        message: t('composites.PartnerDedup.auto_merge_done', { merged: result.merged, skipped: result.skipped }),
      })
      await load({ search: searchQuery.value || undefined })
    }
  })
}
</script>
